/**
 * Runtime app config context.
 *
 * On boot, fetches /api/config which exposes:
 *   - beta_active, beta_full, beta_slots_taken/available
 *   - maintenance_mode  → drives the full-screen "Scaling Pipelines" overlay
 *   - beta_flat_price_usd (display) + beta_flat_price_inr (charged)
 *   - beta_*_storage_gb caps
 *   - supabase_url + supabase_anon_key   → so the frontend can hydrate
 *     Supabase without a rebuild when ops swap creds server-side.
 *
 * Re-polls every 60s so a MAINTENANCE_MODE flip surfaces without a page reload.
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
    beta_active: false,
    beta_is_active: true,
    maintenance_mode: false,
    beta_start_date: null,
    beta_end_date: null,
    beta_user_limit: 1000,
    beta_flat_price_usd: 15,
    beta_flat_price_inr: 1299,
    beta_base_storage_gb: 400,
    beta_unlocked_storage_gb: 600,
    beta_ceiling_storage_gb: 1000,
    beta_slots_taken: 0,
    beta_slots_available: 1000,
    beta_full: false,
    supabase_configured: false,
    supabase_url: '',
    supabase_anon_key: '',
    _loading: true,
    _error: null,
};

const AppConfigContext = createContext(DEFAULTS);

export function AppConfigProvider({ children }) {
    const [cfg, setCfg] = useState(DEFAULTS);
    const timerRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${BACKEND_URL}/api/config`, { timeout: 8000 });
            setCfg({ ...DEFAULTS, ...data, _loading: false, _error: null });
        } catch (e) {
            setCfg((prev) => ({ ...prev, _loading: false, _error: e.message || 'config unreachable' }));
        }
    }, []);

    useEffect(() => {
        load();
        timerRef.current = setInterval(load, 60_000);
        return () => timerRef.current && clearInterval(timerRef.current);
    }, [load]);

    return (
        <AppConfigContext.Provider value={{ ...cfg, reload: load }}>
            {children}
        </AppConfigContext.Provider>
    );
}

export const useAppConfig = () => useContext(AppConfigContext);
